import { PageHero } from "@/components/PageHero";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, getSessionId } from "@/lib/api";
import { toast } from "sonner";
import { BookHeart, Sparkles, ArrowLeft, Loader2 } from "lucide-react";

/* My Story — the caregiver's living summary of their loved one + themselves.
   Designed so caregivers only tell their story once. We auto-merge facts Lane
   extracts from their chats too — see POST /api/my-story/{sid}/extract-from-chat.
*/

const LOVED_ONE_FIELDS = [
    { key: "name", label: "Their name", placeholder: "e.g. Mom, Dad, Marie…", autoFocus: true },
    { key: "relationship", label: "Your relationship", placeholder: "Mom / Dad / Spouse / Partner / Other", type: "select", options: ["Mom", "Dad", "Spouse", "Partner", "Grandparent", "Sibling", "Other"] },
    { key: "age", label: "Their age (roughly)", placeholder: "e.g. 78" },
    { key: "stage", label: "Where you think they are", placeholder: "Early / Middle / Late / Not sure", type: "select", options: ["Early", "Middle", "Late", "Not sure"] },
    { key: "who_they_were", label: "Who they were", placeholder: "A few sentences about who they've always been.", multiline: true },
    { key: "profession", label: "What they did for work", placeholder: "Teacher, nurse, contractor, raised six kids…" },
    { key: "what_they_loved", label: "What they loved", placeholder: "Gardening, jazz, the grandkids, baseball, baking…", multiline: true },
    { key: "what_matters_now", label: "What matters to them now", placeholder: "Music in the afternoons. Knowing the dog. Being in the kitchen.", multiline: true },
    { key: "hobbies", label: "Things that still calm them", placeholder: "Worn-out sweater, hand massage, gospel music…" },
    { key: "medical_notes", label: "Any medical notes you keep forgetting", placeholder: "Allergic to sulfa. UTIs trigger confusion. Hates the dentist.", multiline: true },
];

const CAREGIVER_FIELDS = [
    { key: "name", label: "Your name", placeholder: "What we should call you" },
    { key: "biggest_fear", label: "Your biggest fear right now", placeholder: "A few words is enough.", multiline: true },
    { key: "what_would_help", label: "What would help most this week", placeholder: "A night of sleep. A sibling who calls. Knowing what's coming.", multiline: true },
    { key: "hours_per_week", label: "Hours/week of care you give (roughly)", placeholder: "e.g. 30" },
];

export default function MyStory() {
    const sid = getSessionId();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [extracting, setExtracting] = useState(false);
    const [story, setStory] = useState({
        loved_one: {},
        caregiver: {},
        goals: [],
        challenges: [],
        wizard_completed: false,
        ai_extracted_at: null,
    });
    // The "very simple intake" — minimum facts Memory Lane needs to build a
    // useful Roadmap and Lane responses. Lives next to My Story so caregivers
    // tell their story once and we never ask twice.
    const [intake, setIntake] = useState({
        estimated_stage: "",
        living_situation: "",
        hours_per_week: "",
        main_concerns: [],
        safety_concerns: [],
    });

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const [storyRes, intakeRes] = await Promise.all([
                    api.get(`/my-story/${sid}`),
                    api.get(`/intake/${sid}`).catch(() => ({ data: null })),
                ]);
                if (!active) return;
                setStory({
                    ...storyRes.data,
                    loved_one: storyRes.data.loved_one || {},
                    caregiver: storyRes.data.caregiver || {},
                });
                if (intakeRes.data) {
                    setIntake({
                        estimated_stage: intakeRes.data.estimated_stage || "",
                        living_situation: intakeRes.data.living_situation || "",
                        hours_per_week: intakeRes.data.hours_per_week || "",
                        main_concerns: intakeRes.data.main_concerns || [],
                        safety_concerns: intakeRes.data.safety_concerns || [],
                    });
                }
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [sid]);

    const setLO = (k, v) => setStory(s => ({ ...s, loved_one: { ...s.loved_one, [k]: v } }));
    const setCG = (k, v) => setStory(s => ({ ...s, caregiver: { ...s.caregiver, [k]: v } }));
    const setIN = (k, v) => setIntake(it => ({ ...it, [k]: v }));
    const toggleIN = (k, val) => setIntake(it => {
        const cur = it[k] || [];
        return { ...it, [k]: cur.includes(val) ? cur.filter(x => x !== val) : [...cur, val] };
    });

    const save = async () => {
        setSaving(true);
        try {
            await Promise.all([
                api.put(`/my-story/${sid}`, {
                    session_id: sid,
                    loved_one: story.loved_one,
                    caregiver: story.caregiver,
                    goals: story.goals || [],
                    challenges: story.challenges || [],
                }),
                api.put(`/intake/${sid}`, {
                    session_id: sid,
                    estimated_stage: intake.estimated_stage || "",
                    living_situation: intake.living_situation || "",
                    hours_per_week: Number(intake.hours_per_week) || 0,
                    main_concerns: intake.main_concerns || [],
                    safety_concerns: intake.safety_concerns || [],
                    // Carry through what My Story already knows so we don't
                    // wipe relationship/burnout if Intake was set before.
                    relationship_to_patient: (story.loved_one?.relationship || "").toLowerCase(),
                }),
            ]);
            toast.success("Saved.");
            setStory(s => ({ ...s, wizard_completed: true }));
        } catch (e) {
            toast.error("Couldn't save right now. Try again in a moment.");
        } finally {
            setSaving(false);
        }
    };

    const extractFromChat = async () => {
        setExtracting(true);
        try {
            const r = await api.post(`/my-story/${sid}/extract-from-chat`);
            if (r.data?.updated) {
                const refreshed = await api.get(`/my-story/${sid}`);
                setStory({ ...refreshed.data, loved_one: refreshed.data.loved_one || {}, caregiver: refreshed.data.caregiver || {} });
                toast.success("Pulled in everything you've told Lane so far.");
            } else {
                toast(r.data?.reason || "Nothing new to add from your conversations yet.");
            }
        } catch (e) {
            toast.error("Couldn't reach Lane to pull from your chats.");
        } finally {
            setExtracting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-[#6E7A74] py-12">
                <Loader2 className="animate-spin" size={18} /> Opening your story…
            </div>
        );
    }

    return (
        <div data-clarity-mask="true" className="max-w-3xl space-y-8 animate-fade-in-up">
            <PageHero
                testId="my-story-header"
                kicker="My Story"
                kickerIcon={BookHeart}
                title="Tell us your story — once."
                subtitle={<>Who they are, where things stand, and how you&apos;re holding up. Everything here flows into your Roadmap, your Lane AI conversations, and your Note for the care team. Tell it once, edit it whenever.</>}
            />

            <div className="ml-card p-5 md:p-6 bg-cream-dark/30 !border-cream-dark flex items-start gap-4">
                <Sparkles strokeWidth={1.6} size={20} className="text-teal-dark shrink-0 mt-1" />
                <div className="flex-1">
                    <p className="font-medium text-[#1F2624]">Already told Lane parts of this?</p>
                    <p className="text-sm text-[#4C5551] mt-1">
                        Lane can quietly pull facts you've shared in conversation and pre-fill the answers below.
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="my-story-extract"
                    onClick={extractFromChat}
                    disabled={extracting}
                    className="px-4 py-2 rounded-2xl text-sm font-medium bg-teal-dark text-white hover:bg-teal disabled:opacity-60"
                >
                    {extracting ? "Pulling…" : "Pull from my chats"}
                </button>
            </div>

            <section className="space-y-5">
                <h2 className="text-2xl font-heading font-medium text-[#12776F]">About them</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {LOVED_ONE_FIELDS.map(f => (
                        <Field
                            key={f.key}
                            field={f}
                            value={story.loved_one?.[f.key] || ""}
                            onChange={v => setLO(f.key, v)}
                            full={f.multiline}
                            testid={`mystory-lo-${f.key}`}
                        />
                    ))}
                </div>
            </section>

            <section className="space-y-5" data-testid="mystory-intake">
                <div>
                    <h2 className="text-2xl font-heading font-medium text-[#12776F]">Where things stand</h2>
                    <p className="text-sm text-[#6E7A74] mt-1">
                        A few quick details so Memory Lane can build a roadmap that fits your situation. Nothing here is locked — change it whenever the story changes.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="block">
                        <span className="block text-sm font-medium text-[#4C5551] mb-1.5">Stage (your best guess is fine)</span>
                        <select
                            data-testid="mystory-in-stage"
                            value={intake.estimated_stage || ""}
                            onChange={e => setIN("estimated_stage", e.target.value ? Number(e.target.value) : "")}
                            className="w-full bg-white border border-cream-dark rounded-2xl px-4 py-3 text-[#1F2624] focus:outline-none focus:ring-2 focus:ring-teal-soft"
                        >
                            <option value="">— not sure —</option>
                            <option value="2">Early — forgetfulness, still independent</option>
                            <option value="3">Early — needs reminders</option>
                            <option value="4">Middle — daily help with finances/cooking</option>
                            <option value="5">Middle — help with dressing, hygiene</option>
                            <option value="6">Late — full personal care</option>
                            <option value="7">Late — limited speech/movement</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="block text-sm font-medium text-[#4C5551] mb-1.5">Where they live</span>
                        <select
                            data-testid="mystory-in-living"
                            value={intake.living_situation || ""}
                            onChange={e => setIN("living_situation", e.target.value)}
                            className="w-full bg-white border border-cream-dark rounded-2xl px-4 py-3 text-[#1F2624] focus:outline-none focus:ring-2 focus:ring-teal-soft"
                        >
                            <option value="">— select —</option>
                            <option value="with_me">With me / in our home</option>
                            <option value="alone">Alone at home</option>
                            <option value="with_spouse">With their spouse / partner</option>
                            <option value="assisted_living">Assisted living</option>
                            <option value="memory_care">Memory care</option>
                            <option value="nursing_home">Nursing home</option>
                            <option value="other">Other</option>
                        </select>
                    </label>
                    <label className="block md:col-span-2">
                        <span className="block text-sm font-medium text-[#4C5551] mb-1.5">Hours/week of hands-on care from you (rough)</span>
                        <input
                            type="number" min="0" max="168" step="1"
                            data-testid="mystory-in-hours"
                            value={intake.hours_per_week || ""}
                            onChange={e => setIN("hours_per_week", e.target.value)}
                            placeholder="e.g. 30"
                            className="w-full bg-white border border-cream-dark rounded-2xl px-4 py-3 text-[#1F2624] focus:outline-none focus:ring-2 focus:ring-teal-soft"
                        />
                    </label>
                </div>

                <ChipsField
                    label="What's hardest right now (pick all that apply)"
                    testidPrefix="mystory-in-main"
                    options={[
                        { v: "behaviors", l: "Behavior — sundowning, agitation, repetition" },
                        { v: "sleep", l: "Sleep — theirs or mine" },
                        { v: "safety", l: "Safety — falls, stove, wandering" },
                        { v: "decisions", l: "Big decisions — driving, moving, finances" },
                        { v: "family", l: "Family — siblings disagreeing" },
                        { v: "burnout", l: "I am running on empty" },
                        { v: "end_of_life", l: "End-of-life decisions" },
                    ]}
                    values={intake.main_concerns || []}
                    onToggle={v => toggleIN("main_concerns", v)}
                />

                <ChipsField
                    label="Active safety concerns"
                    testidPrefix="mystory-in-safety"
                    options={[
                        { v: "wandering", l: "Wandering" },
                        { v: "driving", l: "Still driving" },
                        { v: "falls", l: "Falls / unsteady" },
                        { v: "stove", l: "Stove / fire" },
                        { v: "meds", l: "Forgetting meds" },
                        { v: "left_alone", l: "Can't be left alone" },
                        { v: "none", l: "None right now" },
                    ]}
                    values={intake.safety_concerns || []}
                    onToggle={v => toggleIN("safety_concerns", v)}
                />
            </section>


            <section className="space-y-5">
                <h2 className="text-2xl font-heading font-medium text-[#12776F]">About you</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {CAREGIVER_FIELDS.map(f => (
                        <Field
                            key={f.key}
                            field={f}
                            value={story.caregiver?.[f.key] || ""}
                            onChange={v => setCG(f.key, v)}
                            full={f.multiline}
                            testid={`mystory-cg-${f.key}`}                        />
                    ))}
                </div>
            </section>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <Link to="/dashboard" className="text-sm text-[#6E7A74] inline-flex items-center gap-1.5 hover:text-[#4C5551]">
                    <ArrowLeft size={14} /> Back to your dashboard
                </Link>
                <button
                    type="button"
                    onClick={save}
                    data-testid="my-story-save"
                    disabled={saving}
                    className="px-6 py-3 rounded-2xl font-medium bg-teal-dark text-white hover:bg-teal disabled:opacity-60 inline-flex items-center gap-2"
                >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                    Save my story
                </button>
            </div>
        </div>
    );
}

function Field({ field, value, onChange, full, testid }) {
    const common = "w-full bg-white border border-cream-dark rounded-2xl px-4 py-3 text-[#1F2624] placeholder:text-[#6E7A74] focus:outline-none focus:ring-2 focus:ring-teal-soft";
    return (
        <label className={`block ${full ? "md:col-span-2" : ""}`}>
            <span className="block text-sm font-medium text-[#4C5551] mb-1.5">{field.label}</span>
            {field.type === "select" ? (
                <select
                    data-testid={testid}
                    className={common}
                    value={value || ""}
                    onChange={e => onChange(e.target.value)}
                >
                    <option value="">— select —</option>
                    {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
            ) : field.multiline ? (
                <textarea
                    data-testid={testid}
                    className={`${common} min-h-[88px] resize-y`}
                    placeholder={field.placeholder}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                />
            ) : (
                <input
                    data-testid={testid}
                    autoFocus={field.autoFocus}
                    className={common}
                    placeholder={field.placeholder}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                />
            )}
        </label>
    );
}


function ChipsField({ label, options, values, onToggle, testidPrefix }) {
    return (
        <div>
            <p className="text-sm font-medium text-[#4C5551] mb-2">{label}</p>
            <div className="flex flex-wrap gap-2">
                {options.map(o => {
                    const on = values.includes(o.v);
                    return (
                        <button
                            key={o.v}
                            type="button"
                            data-testid={`${testidPrefix}-${o.v}`}
                            onClick={() => onToggle(o.v)}
                            className={`px-3 py-1.5 rounded-2xl border text-sm transition-colors ${on
                                ? "bg-teal-dark border-teal-dark text-white"
                                : "bg-white border-cream-dark text-[#1F2624] hover:bg-cream-dark/30"
                                }`}
                        >
                            {o.l}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
